/**
 * SiliconeButton — signature YUO button.
 *
 * Semi-transparent tinted glass with inner highlight, soft outer glow,
 * bevelled border — reads as "colored silicone" instead of flat.
 *
 * Props:
 *   color     — accent color (defaults to mirror blue)
 *   variant   — "solid" (filled tinted) | "ghost" (barely visible outline)
 *   size      — "sm" | "md" | "lg"
 *   icon      — optional leading element (string or node)
 *   as        — "button" | "div"
 *   testId    — data-testid
 *   ...rest passed to the underlying tag
 */
import { COLORS, FONTS } from "@/lib/constants";
import { tint } from "@/lib/yuoDesign";

const SIZES = {
  sm: { pad: "8px 14px",  font: 12, radius: 999 },
  md: { pad: "11px 20px", font: 13, radius: 999 },
  lg: { pad: "14px 26px", font: 15, radius: 999 },
};

export default function SiliconeButton({
  children, color = COLORS.mirror, variant = "solid", size = "md",
  icon, testId, disabled, as = "button", style = {}, onClick, ...rest
}) {
  const s = SIZES[size] || SIZES.md;
  const Tag = as;

  const solid = variant === "solid";
  const bg = solid
    ? `linear-gradient(180deg, ${tint(color, "44")} 0%, ${tint(color, "22")} 55%, ${tint(color, "18")} 100%)`
    : "transparent";
  const border = solid ? `${tint(color, "88")}` : `${tint(color, "55")}`;

  return (
    <Tag
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        padding: s.pad,
        borderRadius: s.radius,
        fontFamily: FONTS.body,
        fontSize: s.font,
        fontWeight: 600,
        color: solid ? "#F5F5FF" : color,
        background: bg,
        border: `1px solid ${border}`,
        // Silicone illusion: inner top highlight + soft outer glow
        boxShadow: solid
          ? `inset 0 1px 0 ${tint(color, "77")}, inset 0 -6px 12px -6px ${tint(color, "44")}, 0 0 20px -6px ${tint(color, "66")}`
          : `inset 0 1px 0 ${tint(color, "44")}`,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
        letterSpacing: 0.2,
        transition: "transform 160ms cubic-bezier(0.22,1,0.36,1), box-shadow 200ms",
        ...style,
      }}
      onMouseDown={(e) => { if (!disabled) e.currentTarget.style.transform = "translateY(1px)"; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
      {...rest}
    >
      {icon != null && <span style={{ display: "inline-flex", alignItems: "center" }}>{icon}</span>}
      <span>{children}</span>
    </Tag>
  );
}
